import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { 
  User, 
  Briefcase, 
  Users, 
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Camera,
  Upload,
  X,
  Phone,
  MessageCircle
} from "lucide-react";

const STEPS = [
  { id: 1, name: "Personal Info", icon: User },
  { id: 2, name: "Employment", icon: Briefcase },
  { id: 3, name: "Guarantors", icon: Users },
  { id: 4, name: "Review", icon: CheckCircle }
];

export default function NewApplication() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Admin can view form without filling in data
  const isAdmin = user?.role === "admin";
  
  const [formData, setFormData] = useState({
    // Personal Info
    full_name: "",
    age: "",
    gender: "",
    mobile_phone: "",
    whatsapp_phone: "",
    marital_status: "",
    marital_status_other: "",
    national_id: "",
    driver_license: "",
    last_address_1: "",
    last_address_2: "",
    last_tenant_address: "",
    duration_at_last_location: "",
    reason_for_leaving: "",
    past_landlord_phone: "",
    can_landlord_be_contacted: "",
    
    // Employment
    occupation: "",
    occupation_other: "",
    employment_duration: "",
    monthly_salary: "",
    self_employed_returns: "",
    additional_occupants: "",
    rental_duration: "",
    
    // Guarantors
    guarantor_1: {
      name: "",
      phone: "",
      address: "",
      can_be_contacted: ""
    },
    guarantor_2: {
      name: "",
      phone: "",
      address: "",
      can_be_contacted: ""
    }
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleGuarantorChange = (guarantorNum, field, value) => {
    setFormData({
      ...formData,
      [guarantorNum]: { ...formData[guarantorNum], [field]: value }
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }
      setPassportPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPassportPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateStep = (step) => {
    // Admin can skip validation to just view the form
    if (isAdmin) {
      return true;
    }
    
    switch (step) {
      case 1:
        if (!passportPhoto) {
          toast.error("Please upload your passport photo");
          return false;
        }
        if (!formData.full_name || !formData.age || !formData.gender || !formData.marital_status || !formData.national_id) {
          toast.error("Please fill in all required fields");
          return false;
        }
        if (!formData.mobile_phone && !formData.whatsapp_phone) {
          toast.error("Please provide at least one contact number (Mobile or WhatsApp)");
          return false;
        }
        if (formData.marital_status === "Other" && !formData.marital_status_other) {
          toast.error("Please specify your marital status");
          return false;
        }
        return true;
      case 2:
        if (!formData.occupation || !formData.additional_occupants || !formData.rental_duration) {
          toast.error("Please fill in all required fields");
          return false;
        }
        if (formData.occupation === "Other" && !formData.occupation_other) {
          toast.error("Please specify your occupation");
          return false;
        }
        return true;
      case 3:
        if (!formData.guarantor_1.name || !formData.guarantor_1.phone || !formData.guarantor_1.address || !formData.guarantor_1.can_be_contacted) {
          toast.error("Please fill in Guarantor 1 details");
          return false;
        }
        if (!formData.guarantor_2.name || !formData.guarantor_2.phone || !formData.guarantor_2.address || !formData.guarantor_2.can_be_contacted) {
          toast.error("Please fill in Guarantor 2 details");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!passportPhoto && !isAdmin) {
      toast.error("Please upload your passport photo");
      return;
    }
    
    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Add photo
      if (passportPhoto) {
        submitData.append("passport_photo", passportPhoto);
      }
      
      // Add form data
      submitData.append("full_name", formData.full_name);
      submitData.append("age", formData.age);
      submitData.append("gender", formData.gender);
      if (formData.mobile_phone) submitData.append("mobile_phone", formData.mobile_phone);
      if (formData.whatsapp_phone) submitData.append("whatsapp_phone", formData.whatsapp_phone);
      submitData.append("marital_status", formData.marital_status);
      if (formData.marital_status_other) submitData.append("marital_status_other", formData.marital_status_other);
      submitData.append("national_id", formData.national_id);
      if (formData.driver_license) submitData.append("driver_license", formData.driver_license);
      if (formData.last_address_1) submitData.append("last_address_1", formData.last_address_1);
      if (formData.last_address_2) submitData.append("last_address_2", formData.last_address_2);
      if (formData.last_tenant_address) submitData.append("last_tenant_address", formData.last_tenant_address);
      if (formData.duration_at_last_location) submitData.append("duration_at_last_location", formData.duration_at_last_location);
      if (formData.reason_for_leaving) submitData.append("reason_for_leaving", formData.reason_for_leaving);
      if (formData.past_landlord_phone) submitData.append("past_landlord_phone", formData.past_landlord_phone);
      if (formData.can_landlord_be_contacted) submitData.append("can_landlord_be_contacted", formData.can_landlord_be_contacted);
      submitData.append("occupation", formData.occupation);
      if (formData.occupation_other) submitData.append("occupation_other", formData.occupation_other);
      if (formData.employment_duration) submitData.append("employment_duration", formData.employment_duration);
      if (formData.monthly_salary) submitData.append("monthly_salary", formData.monthly_salary);
      if (formData.self_employed_returns) submitData.append("self_employed_returns", formData.self_employed_returns);
      submitData.append("additional_occupants", formData.additional_occupants);
      submitData.append("rental_duration", formData.rental_duration);
      
      // Guarantors as JSON strings
      submitData.append("guarantor_1_name", formData.guarantor_1.name);
      submitData.append("guarantor_1_phone", formData.guarantor_1.phone);
      submitData.append("guarantor_1_address", formData.guarantor_1.address);
      submitData.append("guarantor_1_can_contact", formData.guarantor_1.can_be_contacted);
      submitData.append("guarantor_2_name", formData.guarantor_2.name);
      submitData.append("guarantor_2_phone", formData.guarantor_2.phone);
      submitData.append("guarantor_2_address", formData.guarantor_2.address);
      submitData.append("guarantor_2_can_contact", formData.guarantor_2.can_be_contacted);
      
      await axios.post(`${API}/applications`, submitData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      // Navigate to the application submitted page (shows thank you message)
      navigate("/application-submitted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Photo Upload Section */}
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Legal Name (Surname First) *</Label>
                    <Input
                      id="full_name"
                      data-testid="app-full-name-input"
                      placeholder="e.g., Okonkwo John Chukwuemeka"
                      value={formData.full_name}
                      onChange={(e) => handleChange("full_name", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age">Age *</Label>
                    <Input
                      id="age"
                      data-testid="app-age-input"
                      type="number"
                      placeholder="Enter your age"
                      value={formData.age}
                      onChange={(e) => handleChange("age", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                </div>
              </div>
              
              {/* Photo Upload */}
              <div className="md:w-48 space-y-2">
                <Label className="flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Passport Photo / Selfie *
                </Label>
                <div 
                  className={`relative rounded-xl border-2 border-dashed transition-colors ${
                    photoPreview 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-stone-300 hover:border-primary bg-stone-50 hover:bg-stone-100'
                  }`}
                >
                  {photoPreview ? (
                    <div className="relative">
                      <img 
                        src={photoPreview} 
                        alt="Passport preview" 
                        className="w-full h-48 object-cover rounded-lg"
                        data-testid="app-photo-preview"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        data-testid="app-remove-photo-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 left-2 right-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Change Photo
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <label 
                      className="flex flex-col items-center justify-center h-48 cursor-pointer p-4"
                      data-testid="app-photo-upload-area"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                        <Upload className="w-6 h-6 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-stone-700 text-center">Click to upload</span>
                      <span className="text-xs text-stone-500 text-center mt-1">or drag and drop</span>
                      <span className="text-xs text-stone-400 text-center mt-2">PNG, JPG up to 5MB</span>
                    </label>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    data-testid="app-photo-input"
                  />
                </div>
                {!passportPhoto && !isAdmin && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    Photo is required
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gender *</Label>
                <Select value={formData.gender} onValueChange={(value) => handleChange("gender", value)}>
                  <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="app-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Marital Status *</Label>
                <Select value={formData.marital_status} onValueChange={(value) => handleChange("marital_status", value)}>
                  <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="app-marital-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Married">Married</SelectItem>
                    <SelectItem value="Single">Single</SelectItem>
                    <SelectItem value="Widowed">Widowed</SelectItem>
                    <SelectItem value="Divorced">Divorced</SelectItem>
                    <SelectItem value="Other">Other (state)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Contact Info Section */}
            <fieldset className="border border-stone-200 rounded-xl p-4">
              <legend className="px-2 text-sm font-medium text-primary flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Contact Info (at least one required)
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mobile_phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-stone-500" />
                    Mobile Phone #
                  </Label>
                  <Input
                    id="mobile_phone"
                    data-testid="app-mobile-phone-input"
                    placeholder="e.g., +234 801 234 5678"
                    value={formData.mobile_phone}
                    onChange={(e) => handleChange("mobile_phone", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whatsapp_phone" className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-green-600" />
                    WhatsApp # (preferred)
                  </Label>
                  <Input
                    id="whatsapp_phone"
                    data-testid="app-whatsapp-phone-input"
                    placeholder="e.g., +234 801 234 5678"
                    value={formData.whatsapp_phone}
                    onChange={(e) => handleChange("whatsapp_phone", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>
              </div>
              <p className="text-xs text-stone-500 mt-2">
                WhatsApp is preferred for faster communication
              </p>
            </fieldset>
            
            {formData.marital_status === "Other" && (
              <div className="space-y-2">
                <Label htmlFor="marital_status_other">Specify Marital Status *</Label>
                <Input
                  id="marital_status_other"
                  data-testid="app-marital-status-other-input"
                  placeholder="Please specify"
                  value={formData.marital_status_other}
                  onChange={(e) => handleChange("marital_status_other", e.target.value)}
                  className="h-12 bg-stone-50 border-stone-200"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="national_id">National Identity Card Number *</Label>
                <Input
                  id="national_id"
                  data-testid="app-national-id-input"
                  placeholder="Enter NIN"
                  value={formData.national_id}
                  onChange={(e) => handleChange("national_id", e.target.value)}
                  className="h-12 bg-stone-50 border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver_license">Driver License Number (if applicable)</Label>
                <Input
                  id="driver_license"
                  data-testid="app-driver-license-input"
                  placeholder="Enter driver license"
                  value={formData.driver_license}
                  onChange={(e) => handleChange("driver_license", e.target.value)}
                  className="h-12 bg-stone-50 border-stone-200"
                />
              </div>
            </div>

            <fieldset className="border border-stone-200 rounded-xl p-4">
              <legend className="px-2 text-sm font-medium text-primary">Previous Addresses</legend>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="last_address_1">Last Address 1 (if applicable)</Label>
                  <Textarea
                    id="last_address_1"
                    data-testid="app-last-address-1-input"
                    placeholder="Enter previous address"
                    value={formData.last_address_1}
                    onChange={(e) => handleChange("last_address_1", e.target.value)}
                    className="bg-stone-50 border-stone-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_address_2">Last Address 2 (if applicable)</Label>
                  <Textarea
                    id="last_address_2"
                    data-testid="app-last-address-2-input"
                    placeholder="Enter previous address"
                    value={formData.last_address_2}
                    onChange={(e) => handleChange("last_address_2", e.target.value)}
                    className="bg-stone-50 border-stone-200"
                  />
                </div>
              </div>
            </fieldset>

            <fieldset className="border border-stone-200 rounded-xl p-4">
              <legend className="px-2 text-sm font-medium text-primary">Last Tenancy Details</legend>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="last_tenant_address">Address of Last Location as Tenant</Label>
                  <Textarea
                    id="last_tenant_address"
                    data-testid="app-last-tenant-address-input"
                    placeholder="Enter address"
                    value={formData.last_tenant_address}
                    onChange={(e) => handleChange("last_tenant_address", e.target.value)}
                    className="bg-stone-50 border-stone-200"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="duration_at_last_location">Duration of Stay</Label>
                    <Input
                      id="duration_at_last_location"
                      data-testid="app-duration-input"
                      placeholder="e.g., 2 years"
                      value={formData.duration_at_last_location}
                      onChange={(e) => handleChange("duration_at_last_location", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reason_for_leaving">Reason for Leaving/Moving</Label>
                    <Input
                      id="reason_for_leaving"
                      data-testid="app-reason-leaving-input"
                      placeholder="Enter reason"
                      value={formData.reason_for_leaving}
                      onChange={(e) => handleChange("reason_for_leaving", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="past_landlord_phone">Past Landlord Phone #</Label>
                    <Input
                      id="past_landlord_phone"
                      data-testid="app-landlord-phone-input"
                      placeholder="Enter phone number"
                      value={formData.past_landlord_phone}
                      onChange={(e) => handleChange("past_landlord_phone", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Can the Landlord be Contacted?</Label>
                    <Select value={formData.can_landlord_be_contacted} onValueChange={(value) => handleChange("can_landlord_be_contacted", value)}>
                      <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="app-can-contact-landlord-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Occupation *</Label>
                <Select value={formData.occupation} onValueChange={(value) => handleChange("occupation", value)}>
                  <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="app-occupation-select">
                    <SelectValue placeholder="Select occupation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Civil servant">Civil servant</SelectItem>
                    <SelectItem value="Self Employed">Self Employed</SelectItem>
                    <SelectItem value="Trader">Trader</SelectItem>
                    <SelectItem value="Student">Student</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.occupation === "Other" && (
                <div className="space-y-2">
                  <Label htmlFor="occupation_other">Specify Occupation *</Label>
                  <Input
                    id="occupation_other"
                    data-testid="app-occupation-other-input"
                    placeholder="Please specify"
                    value={formData.occupation_other}
                    onChange={(e) => handleChange("occupation_other", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>
              )}
            </div>

            {(formData.occupation === "Civil servant" || formData.occupation === "Other") && (
              <fieldset className="border border-stone-200 rounded-xl p-4">
                <legend className="px-2 text-sm font-medium text-primary">Employment Details</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employment_duration">How Long Employed?</Label>
                    <Input
                      id="employment_duration"
                      data-testid="app-employment-duration-input"
                      placeholder="e.g., 5 years"
                      value={formData.employment_duration}
                      onChange={(e) => handleChange("employment_duration", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthly_salary">Monthly Salary (₦)</Label>
                    <Input
                      id="monthly_salary"
                      data-testid="app-monthly-salary-input"
                      placeholder="e.g., 250,000"
                      value={formData.monthly_salary}
                      onChange={(e) => handleChange("monthly_salary", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                </div>
              </fieldset>
            )}

            {(formData.occupation === "Self Employed" || formData.occupation === "Trader") && (
              <fieldset className="border border-stone-200 rounded-xl p-4">
                <legend className="px-2 text-sm font-medium text-primary">Business Details</legend>
                <div className="space-y-2">
                  <Label htmlFor="self_employed_returns">Average Monthly Returns (₦)</Label>
                  <Input
                    id="self_employed_returns"
                    data-testid="app-monthly-returns-input"
                    placeholder="e.g., 300,000"
                    value={formData.self_employed_returns}
                    onChange={(e) => handleChange("self_employed_returns", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>
              </fieldset>
            )}

            <fieldset className="border border-stone-200 rounded-xl p-4">
              <legend className="px-2 text-sm font-medium text-primary">Rental Information</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Additional Occupants (excluding spouse) *</Label>
                  <Select value={formData.additional_occupants} onValueChange={(value) => handleChange("additional_occupants", value)}>
                    <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="app-additional-occupants-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0</SelectItem>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rental_duration">How Long Do You Plan to Rent? *</Label>
                  <Input
                    id="rental_duration"
                    data-testid="app-rental-duration-input"
                    placeholder="e.g., 2 years"
                    value={formData.rental_duration}
                    onChange={(e) => handleChange("rental_duration", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>
              </div>
            </fieldset>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            {/* Guarantor 1 */}
            <fieldset className="border border-stone-200 rounded-xl p-4">
              <legend className="px-2 text-sm font-medium text-primary">Guarantor 1</legend>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="g1_name">Full Name *</Label>
                  <Input
                    id="g1_name"
                    data-testid="app-guarantor1-name-input"
                    placeholder="Enter guarantor's full name"
                    value={formData.guarantor_1.name}
                    onChange={(e) => handleGuarantorChange("guarantor_1", "name", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="g1_phone">Contact Phone # *</Label>
                    <Input
                      id="g1_phone"
                      data-testid="app-guarantor1-phone-input"
                      placeholder="Enter phone number"
                      value={formData.guarantor_1.phone}
                      onChange={(e) => handleGuarantorChange("guarantor_1", "phone", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Can the Guarantor be Contacted? *</Label>
                    <Select 
                      value={formData.guarantor_1.can_be_contacted} 
                      onValueChange={(value) => handleGuarantorChange("guarantor_1", "can_be_contacted", value)}
                    >
                      <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="app-guarantor1-contact-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="g1_address">Address *</Label>
                  <Textarea
                    id="g1_address"
                    data-testid="app-guarantor1-address-input"
                    placeholder="Enter guarantor's address"
                    value={formData.guarantor_1.address}
                    onChange={(e) => handleGuarantorChange("guarantor_1", "address", e.target.value)}
                    className="bg-stone-50 border-stone-200"
                  />
                </div>
              </div>
            </fieldset>

            {/* Guarantor 2 */}
            <fieldset className="border border-stone-200 rounded-xl p-4">
              <legend className="px-2 text-sm font-medium text-primary">Guarantor 2</legend>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="g2_name">Full Name *</Label>
                  <Input
                    id="g2_name"
                    data-testid="app-guarantor2-name-input"
                    placeholder="Enter guarantor's full name"
                    value={formData.guarantor_2.name}
                    onChange={(e) => handleGuarantorChange("guarantor_2", "name", e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="g2_phone">Contact Phone # *</Label>
                    <Input
                      id="g2_phone"
                      data-testid="app-guarantor2-phone-input"
                      placeholder="Enter phone number"
                      value={formData.guarantor_2.phone}
                      onChange={(e) => handleGuarantorChange("guarantor_2", "phone", e.target.value)}
                      className="h-12 bg-stone-50 border-stone-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Can the Guarantor be Contacted? *</Label>
                    <Select 
                      value={formData.guarantor_2.can_be_contacted} 
                      onValueChange={(value) => handleGuarantorChange("guarantor_2", "can_be_contacted", value)}
                    >
                      <SelectTrigger className="h-12 bg-stone-50 border-stone-200" data-testid="app-guarantor2-contact-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="g2_address">Address *</Label>
                  <Textarea
                    id="g2_address"
                    data-testid="app-guarantor2-address-input"
                    placeholder="Enter guarantor's address"
                    value={formData.guarantor_2.address}
                    onChange={(e) => handleGuarantorChange("guarantor_2", "address", e.target.value)}
                    className="bg-stone-50 border-stone-200"
                  />
                </div>
              </div>
            </fieldset>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-stone-50 rounded-xl p-6">
              <h3 className="font-semibold text-primary mb-4">Personal Information</h3>
              <div className="flex gap-6">
                {/* Photo Preview */}
                {photoPreview && (
                  <div className="flex-shrink-0">
                    <img 
                      src={photoPreview} 
                      alt="Passport Photo" 
                      className="w-32 h-32 object-cover rounded-xl border-2 border-stone-200"
                    />
                  </div>
                )}
                <div className="flex-1 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Full Name:</span>
                    <p className="font-medium">{formData.full_name}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Age:</span>
                    <p className="font-medium">{formData.age}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Gender:</span>
                    <p className="font-medium">{formData.gender}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Marital Status:</span>
                    <p className="font-medium">{formData.marital_status} {formData.marital_status_other && `(${formData.marital_status_other})`}</p>
                  </div>
                  {formData.mobile_phone && (
                    <div>
                      <span className="text-text-muted">Mobile Phone:</span>
                      <p className="font-medium">{formData.mobile_phone}</p>
                    </div>
                  )}
                  {formData.whatsapp_phone && (
                    <div>
                      <span className="text-text-muted">WhatsApp:</span>
                      <p className="font-medium">{formData.whatsapp_phone}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-text-muted">National ID:</span>
                    <p className="font-medium">{formData.national_id}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-xl p-6">
              <h3 className="font-semibold text-primary mb-4">Employment Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-text-muted">Occupation:</span>
                  <p className="font-medium">{formData.occupation} {formData.occupation_other && `(${formData.occupation_other})`}</p>
                </div>
                {formData.monthly_salary && (
                  <div>
                    <span className="text-text-muted">Monthly Salary:</span>
                    <p className="font-medium">₦{formData.monthly_salary}</p>
                  </div>
                )}
                {formData.self_employed_returns && (
                  <div>
                    <span className="text-text-muted">Monthly Returns:</span>
                    <p className="font-medium">₦{formData.self_employed_returns}</p>
                  </div>
                )}
                <div>
                  <span className="text-text-muted">Additional Occupants:</span>
                  <p className="font-medium">{formData.additional_occupants}</p>
                </div>
                <div>
                  <span className="text-text-muted">Planned Rental Duration:</span>
                  <p className="font-medium">{formData.rental_duration}</p>
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-xl p-6">
              <h3 className="font-semibold text-primary mb-4">Guarantors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Guarantor 1</h4>
                  <p><span className="text-text-muted">Name:</span> {formData.guarantor_1.name}</p>
                  <p><span className="text-text-muted">Phone:</span> {formData.guarantor_1.phone}</p>
                  <p><span className="text-text-muted">Can be contacted:</span> {formData.guarantor_1.can_be_contacted}</p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Guarantor 2</h4>
                  <p><span className="text-text-muted">Name:</span> {formData.guarantor_2.name}</p>
                  <p><span className="text-text-muted">Phone:</span> {formData.guarantor_2.phone}</p>
                  <p><span className="text-text-muted">Can be contacted:</span> {formData.guarantor_2.can_be_contacted}</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <p className="text-sm text-yellow-800">
                By submitting this application, you confirm that all the information provided is accurate and complete. 
                False information may result in the rejection of your application.
              </p>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="new-application-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary">New Tenant Application</h1>
            <p className="text-text-muted mt-1">Complete all steps to submit your application</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center ${index > 0 ? 'flex-1' : ''}`}>
                    {index > 0 && (
                      <div className={`h-1 w-full mx-2 ${currentStep > index ? 'bg-primary' : 'bg-stone-200'}`} />
                    )}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep > step.id 
                        ? 'bg-primary text-white' 
                        : currentStep === step.id 
                        ? 'bg-primary text-white ring-4 ring-primary/20' 
                        : 'bg-stone-200 text-stone-500'
                    }`}>
                      <step.icon className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm">
              {STEPS.map((step) => (
                <span key={step.id} className={`${currentStep >= step.id ? 'text-primary font-medium' : 'text-text-muted'}`}>
                  {step.name}
                </span>
              ))}
            </div>
            <Progress value={(currentStep / STEPS.length) * 100} className="mt-4" />
          </div>

          {/* Form Card */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-xl">{STEPS[currentStep - 1].name}</CardTitle>
              <CardDescription>
                {currentStep === 1 && "Enter your personal details"}
                {currentStep === 2 && "Provide your employment information"}
                {currentStep === 3 && "Add two guarantors for your application"}
                {currentStep === 4 && "Review your application before submitting"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Admin Preview Notice */}
              {isAdmin && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>Admin Preview Mode:</strong> You are viewing this form as an administrator. You can navigate through all pages without filling in any information.
                  </p>
                </div>
              )}
              
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {currentStep > 1 ? (
                  <Button 
                    variant="outline" 
                    onClick={prevStep}
                    className="rounded-full"
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                ) : (
                  <div />
                )}
                
                {currentStep < STEPS.length ? (
                  <Button 
                    onClick={nextStep}
                    className="rounded-full bg-primary hover:bg-primary-800"
                    data-testid="next-step-btn"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : isAdmin ? (
                  <Button 
                    onClick={() => navigate("/dashboard")}
                    className="rounded-full bg-primary hover:bg-primary-800"
                    data-testid="back-to-dashboard-btn"
                  >
                    Back to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit}
                    className="rounded-full bg-primary hover:bg-primary-800"
                    disabled={loading}
                    data-testid="submit-application-btn"
                  >
                    {loading ? "Submitting..." : "Submit Application"}
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
